import {useDispatch} from 'react-redux';
import {
  setAddResourcesError,
  setAddResourcesLoading,
  setHasSimulationTasks,
  setMappedResources,
  setMappedResourcesError,
  setMappedResourcesLoading,
  setNotificationSetting,
  setNotificationSettingError,
  setNotificationSettingLoading,
  setProjectHistory,
  setProjectHistoryError,
  setProjectHistoryLoading,
  setProjectIntegrations,
  setProjectIntegrationsError,
  setProjectIntegrationsLoading,
  setProjects,
  setProjectsError,
  setProjectShare,
  setProjectShareError,
  setProjectShareLoading,
  setProjectsLoading,
  setProjectStats,
  setProjectStatsError,
  setProjectStatsLoading,
  setSaveMappedResourcesError,
  setSaveMappedResourcesLoading,
  setSimulationMergeError,
  setSimulationMergeLoading,
  setSingleProjectData,
  setSingleProjectError,
  setSingleProjectLoading,
  setSyncWithJiraError,
  setSyncWithJiraLoading,
  setTask,
  setTaskError,
  setTaskList,
  setTaskListError,
  setTaskListLoading,
  setTaskLoading,
  setTasksError,
  setTasksLoading,
  setUpdateSimulationError,
  setUpdateSimulationLoading,
  setVersions,
  setVersionsError,
  setVersionsLoading
} from '../redux/slices/projects';
import {setRoadmapCPLI} from '../redux/slices/roadmap';
import {useRoadmapService, useTemplateService} from '../services';
import axios from '../utils/axiosConfig';
import {errorHandler} from '../utils/errorHandler';
import {setOrganizationData, setOrganizationError, setOrganizationLoading,} from '../redux/slices/organization';
import toast from "react-hot-toast";


interface Dependencies {
  created_at: Date;
  from_task: string;
  item_id: string;
  modified_at: Date;
  project_id: string;
  to_task: string;
}

interface Tasks {
  completion: number;
  created_at: Date;
  duration: number;
  item_id: string;
  modified_at: Date;
  project_id: string;
  responsible: string;
  skill: string;
}

interface Project {
  dependencies: [Dependencies],
  org_id: "",
  project_id: "",
  project_name: "",
  tasks: [Tasks]
}

interface CreateProject {
  org_id?: string;
  project_name?: string;
  stage?: string;
  members?:any[];
  share_link?:any;
}
interface CreateProjectBySimulation {
  org_id?: string;
  project_description?: string;
}

interface TaskCompletion {
  completion?: number;
  skill?: string;
  description?: string;
  duration?: string;
  responsible?: string;
  start_date?: string;
}
interface CreateTask {
  completion: string;
}

interface Organization {
  org_id: string;
  name: string;
  created_at: Date;
  modified_at: Date;
}

const useProjectService = () => {
  const dispatch = useDispatch();
  const {fetchData} = useRoadmapService()
  const {getTemplateById} = useTemplateService()

  // Helper function to ensure resource IDs are strings
  const ensureResourceIdIsString = (payload: any) => {
    const updatedPayload = {...payload};

    // Handle members array (resource IDs)
    if (updatedPayload.members && Array.isArray(updatedPayload.members)) {
      updatedPayload.members = updatedPayload.members.map((id: any) => id.toString());
    }
    // Handle single resource/member ID
    else if (updatedPayload.member_id) {
      updatedPayload.member_id = updatedPayload.member_id.toString();
    }

    return updatedPayload;
  };

  const showError = (error: any) => {
    errorHandler.showError(error);
  };


  /**
   * Checks if a project has simulation tasks
   * @param project_id - The project ID
   * @returns Promise with boolean indicating if simulation tasks exist
   */
  const checkSimulationTasks = async (project_id: string): Promise<boolean | undefined> => {
    if (!project_id) return undefined;

    dispatch(setTaskListLoading(true));
    try {
      const API_URL = `/simulation/${project_id}/data`;
      const response = await axios.post(API_URL, {
        response_type: "list",
      });

      const hasSimTasks = !!(response.data &&
        response.data.sim_data &&
        Array.isArray(response.data.sim_data.tasks) &&
        response.data.sim_data.tasks.length > 0);

      dispatch(setHasSimulationTasks(hasSimTasks));
      return hasSimTasks;
    } catch (error) {
      console.error("Error checking simulation tasks:", error);
      dispatch(setHasSimulationTasks(false));
      return false;
    } finally {
      dispatch(setTaskListLoading(false));
    }
  };

  const fetchAllProjects = async (org_id: any) => {
    dispatch(setOrganizationLoading(true));
    try {
      const response = await axios.get<Organization[]>(`/organization/${org_id}/projects`);
      dispatch(setOrganizationData(response.data));
    } catch (error) {
      showError(error)
      dispatch(setOrganizationError(error));
    } finally {
      dispatch(setOrganizationLoading(false));
    }
  };

  const deleteProjects = async (project_id: string,org_id:string) => {
    dispatch(setOrganizationLoading(true));
    try {
      const  data = {project_id,org_id};
      const config = {
        method: 'delete',
        url: `/project`,
        data
      };
      const response = await axios(config)
      if(response.data){
        await fetchAllProjects(org_id);
        toast.success(response.data.message);
      }
    } catch (error) {
      showError(error)
      dispatch(setOrganizationError(error));
    } finally {
      dispatch(setOrganizationLoading(false));
    }
  };



  const fetchSingleProject = async (project_id: string) => {
    dispatch(setSingleProjectLoading(true));
    try {
      if(!project_id){
        return false;
      }
      const response =  await axios.get<Project[]>(`/project/${project_id}?sim_flag=false`);
      const projData:any = response.data
      dispatch(setSingleProjectData(projData));
    } catch (error: any) {
      // Check if it's a 404 error (project not found)
      if (error?.response?.status === 404) {
        toast.error("Project not found. Redirecting to projects page.");
        // Redirect to projects page after a short delay
        setTimeout(() => {
          window.location.href = "/workitems";
        }, 1000);
      } else {
        showError(error);
      }
      dispatch(setSingleProjectError(error));
    } finally {
      dispatch(setSingleProjectLoading(false));
    }
  };

  const clearSingleProject = async () => {
    dispatch(setSingleProjectData({}));
  };


  const fetchProject = async (project_id: string,sim_flag:boolean=false) => {
    dispatch(setProjectsLoading(true));
    try {
      if(!project_id){
        return false;
      }
      const response =  await axios.get<Project[]>(`/project/${project_id}?sim_flag=${sim_flag}`);
      const resourceIdsObj:any = {}
      const projData:any = response.data
      if(projData.tasks){
        const data  = projData.tasks.filter((ele:any)=>ele.responsible !=="" && ele.responsible !==null)
        const resourceIds:any[]= data.map((ele:any)=>ele.responsible)
        if(resourceIds.length){
          const resourcesRes = await axios.post(`/resources/getResourcesInfo`, {resource_id:resourceIds});
          if(resourcesRes.data){
            resourcesRes.data.forEach((ele:any)=>{
              resourceIdsObj[ele.id]= ele
            })
          }
        }
        const tasks = [...projData.tasks]
        const maptasksData = tasks.map((ele:any)=>{
          return {
            ...ele,
            resourcesData:resourceIdsObj[ele.responsible]
          }
        })
        projData.tasks = maptasksData
      }
      dispatch(setProjects(projData));
    } catch (error: any) {
      // Check if it's a 404 error (project not found)
      if (error?.response?.status === 404) {
        toast.error("Project not found. Redirecting to projects page.");
        // Redirect to projects page after a short delay
        setTimeout(() => {
          window.location.href = "/workitems";
        }, 1000);
      } else {
        showError(error);
      }
      dispatch(setProjectsError(error));
    } finally {
      dispatch(setProjectsLoading(false));
    }
  };

  const createProject = async (project: CreateProject, preventRedirect: boolean = false) => {
    dispatch(setProjectsLoading(true));
    try {
      const response:any = await axios.post(`/project`, project);
      if(response.data){
        if(response.data.project_id && !preventRedirect){
          window.location.href = `/projects/${response.data.project_id}?isnewproject=true`
        } else {
          await fetchAllProjects(project?.org_id);
        }
      }
      return response.data; // Return data for caller to handle

    } catch (error) {
      showError(error)
      dispatch(setProjectsError(error));
      throw error;
      return undefined;
    } finally {
      dispatch(setProjectsLoading(false));
    }
  };
  const createProjectBySimulation = async (project: CreateProjectBySimulation) => {
    dispatch(setProjectsLoading(true));
    try {
      const response:any = await axios.post(`/project/simulation`, project);
      toast.success(response.data.message);
      if(response.data){
        if(response.data.project_id){
          window.location.href = `/projects/${response.data.project_id}`
        } else {
          await fetchAllProjects(project?.org_id);
        }

      }

    } catch (error) {
      showError(error)
      dispatch(setProjectsError(error));
    } finally {
      dispatch(setProjectsLoading(false));
    }
  };
  const createTask = async (project_id: string, task: CreateTask) => {
    dispatch(setTasksLoading(true));
    try {
      await axios.post(`/project/${project_id}/task`, task);
      // fetchProject(project_id);
      // getTemplateById(project_id);
    } catch (error) {
      showError(error)
      dispatch(setTasksError(error));
    } finally {
      dispatch(setTasksLoading(false));
    }
  };

  const updateProject = async (project_id: string, project: any) => {
    const {
      isFetchAllProjects = true,
      suppressErrorToast = false,
      throwOnError = false,
      ...payload
    } = project || {};

    if (isFetchAllProjects) {
      dispatch(setProjectsLoading(true));
    }
    try {
      
    const updatedProject = ensureResourceIdIsString(payload);
    const toast_promise = toast.promise(
      axios.put(`/project/${project_id}`, updatedProject),
      {
        loading: "Updating Item...",
        success: (response: any) =>
          response.data.message || "Item updated successfully!",
        error: (error: any) =>
          error.response?.data?.detail || "Failed to update Item.",
      },
    );
    const response = await toast_promise;


      // if (isFetchAllProjects) {
      //   // toast.success(response.data.message);
      //   // await fetchAllProjects(project.org_id);
      // }
      return response.data;
    } catch (error) {
      if (!suppressErrorToast) {
        showError(error);
      }
      dispatch(setProjectsError(error));
      if (throwOnError) throw error;
      return undefined;
    } finally {
      dispatch(setProjectsLoading(false));
    }
  };

  const updateTaskCompletion = async (
    project_id: string,
    task_id: string,
    payload: TaskCompletion,
    options?: { suppressErrorToast?: boolean; throwOnError?: boolean; suppressSuccessToast?: boolean }
  ) => {
    dispatch(setProjectsLoading(true));
    try {
      const response  = await axios.put(`/project/${project_id}/task/${task_id}`, payload);
      if(response.data?.error){
        if(!options?.suppressErrorToast){
          toast.error(response.data.error);
        }
        if (options?.throwOnError) throw new Error(response.data.error);
        return response.data;
      } else {
        if(!options?.suppressSuccessToast){
          toast.success(response.data.message);
        }
        return response.data;
      }

    } catch (error) {
      if(!options?.suppressErrorToast){
        showError(error)
      }
      dispatch(setProjectsError(error));
      if (options?.throwOnError) throw error;
      return undefined;
    } finally {
      dispatch(setProjectsLoading(false));
    }
  };

  const fetchTask = async (project_id: string,task_id?:string,sim_flag?:boolean) => {
    dispatch(setTaskLoading(true));
    try {
      const respons =  axios.get<Project[]>(`/project/${project_id}/task/${task_id}?sim_flag=${sim_flag}`);
      const response =  await respons
      dispatch(setTask(response.data));
    } catch (error) {
      showError(error)
      dispatch(setTaskError(error));
    } finally {
      dispatch(setTaskLoading(false));
    }
  };

  const deleteTask = async (project_id: string,task_id?:string,sim_flag?:any) => {
    dispatch(setTaskLoading(true));
    try {
      const response =  await axios.delete(`/project/${project_id}/task/${task_id}?sim_flag=${sim_flag}`);
      if(response.data.error){
        toast.error(response.data.error);
      } else {
        toast.success(response.data.message);
        // fetchProject(project_id);
        // getTemplateById(project_id);
      }
    } catch (error) {
      showError(error)
      dispatch(setTaskError(error));
    } finally {
      dispatch(setTaskLoading(false));
    }
  };

  const addResources = async (project_id: string, resource:any) => {
    dispatch(setAddResourcesLoading(true));
    try {
      await axios.post(`/project/${project_id}/resource`, resource);
      fetchProject(project_id);
    } catch (error) {
      showError(error)
      dispatch(setAddResourcesError(error));
    } finally {
      dispatch(setAddResourcesLoading(false));
    }
  };
  const getMappedResources = async (project_id: string, query:any) => {
    dispatch(setMappedResourcesLoading(true));
    try {
      const updatedQuery = ensureResourceIdIsString(query);
      const response = await axios.post(`/project/${project_id}/map_members`, updatedQuery);
      dispatch(setMappedResources(response.data.members));
      toast.success(response.data.message);
    } catch (error) {
      showError(error)
      dispatch(setMappedResourcesError(error));
    } finally {
      dispatch(setMappedResourcesLoading(false));
    }
  };

  const saveMappedResources = async (project_id: string, query:any) => {
    dispatch(setSaveMappedResourcesLoading(true));
    try {
      const updatedQuery = ensureResourceIdIsString(query);
      const response = await axios.post(`/project/${project_id}/members`, updatedQuery);
      toast.success(response.data.message);
    } catch (error) {
      showError(error)
      dispatch(setSaveMappedResourcesError(error));
    } finally {
      dispatch(setSaveMappedResourcesLoading(false));
    }
  };

  const saveSimulationMerge = async (project_id: string,sim_flag?:any) => {
    dispatch(setSimulationMergeLoading(true));
    try {
      const response = await axios.post(`/simulation/${project_id}/merge`);
      fetchProject(project_id,sim_flag);
      toast.success(response.data.message);
    } catch (error) {
      showError(error)
      dispatch(setSimulationMergeError(error));
    } finally {
      dispatch(setSimulationMergeLoading(false));
    }
  };

  const sendSimulationReset = async (project_id: string,sim_flag?:any) => {
    dispatch(setSimulationMergeLoading(true));
    try {
      const response = await axios.delete(`/simulation/${project_id}/reset`);
      fetchProject(project_id,sim_flag);
      toast.success(response.data.message);
    } catch (error) {
      showError(error)
      dispatch(setSimulationMergeError(error));
    } finally {
      dispatch(setSimulationMergeLoading(false));
    }
  };

  const fetchTaskData = async (project_id: any,sim_flag?:boolean,query?:any ) => {
    if(!project_id) return
    const API_URL = `/simulation/${project_id}/data`
    dispatch(setTaskListLoading(true));
    try {
      // First fetch the data for the current mode (live or simulation)
      const response = await axios.post(API_URL,{
        ...query,
        sim_flag: sim_flag || false,
      });

      const raw: any = response.data || {};
      const sourceTasks: any[] = sim_flag ? (raw?.sim_data?.tasks || []) : (raw?.tasks || []);

      const mappedTasks = sourceTasks.map((t: any) => {
        const id = t?.item_id ?? t?.id ?? t?.task_id ?? '';
        const title = t?.task_title ?? t?.name ?? t?.title ?? '';
        const type = t?.task_type ?? t?.type ?? '';
        const memberObj = (() => {
        
          if (t?.member && (t.member.name || t.member.id)) return t.member;
          if (t?.resourcesData) {
            return {
              id: t?.resourcesData?.id ?? t?.responsible ?? null,
              name: t?.resourcesData?.name ?? '',
              image_url: t?.resourcesData?.image_url ?? undefined,
            };
          }
          if (t?.responsible) {
            return { id: t.responsible, name: String(t.responsible), image_url: undefined };
          }
          return { id: null, name: '', image_url: undefined };
        })();

        const deps = Array.isArray(t?.dependency)
          ? t.dependency
          : (Array.isArray(t?.dependencies)
              ? t.dependencies.map((d: any) => d?.item_id ?? d?.to_task ?? d?.from_task).filter(Boolean)
              : []);

        const start = t?.adj_start_date ?? t?.start ?? t?.start_date ?? null;
        const completion = typeof t?.completion === 'number' ? t.completion
          : (typeof t?.progress === 'number' ? t.progress : 0);

        return {
          ...t,
          item_id: id,
          task_title: title,
          task_type: type,
          dependency: deps,
          duration: t?.duration ?? (t?.start && t?.end ? Math.max(0, Math.ceil((new Date(t.end).getTime() - new Date(t.start).getTime()) / (1000*60*60*24))) : t?.duration),
          member: memberObj,
          skill: t?.skill ?? t?.skill_name ?? '',
          adj_start_date: start,
          completion,
        };
      });

      dispatch(setTaskList({ tasks: mappedTasks }));
      dispatch(setRoadmapCPLI(raw));

      // Only check for simulation tasks when in live mode with no tasks
      if (!sim_flag) {
        const hasTasks = mappedTasks.length > 0;

        if (!hasTasks) {
          // No live tasks, check if simulation tasks exist
          const hasSimTasks = await checkSimulationTasks(project_id);
          // No need to dispatch here as checkSimulationTasks already does it
        } else {
          // We have live tasks, so no need to show simulation message
          dispatch(setHasSimulationTasks(false));
        }
      }
    } catch (error) {
      showError(error)
      dispatch(setTaskListError(error));
    } finally {
      dispatch(setTaskListLoading(false));
    }
  };

  const fetchProjectVersion= async (project_id: string) => {
    dispatch(setVersionsLoading(true));
    try {
      const respons =  axios.get<Project[]>(`/project/${project_id}/versions`);
      const response =  await respons
      dispatch(setVersions(response.data));
    } catch (error) {
      showError(error)
      dispatch(setVersionsError(error));
    } finally {
      dispatch(setVersionsLoading(false));
    }
  };

  const syncWithJira= async (project_id: string, query?:any) => {
    dispatch(setSyncWithJiraLoading(true));
    try {
      const respons =  axios.post<any>(`/project/${project_id}/sync_jira`,query);
      const response =  await respons;
      toast.success(response.data.message);
    } catch (error) {
      showError(error)
      dispatch(setSyncWithJiraError(error));
    } finally {
      dispatch(setSyncWithJiraLoading(false));
    }
  };
  const syncWithAsana= async (project_id: string, query?:any) => {
    dispatch(setSyncWithJiraLoading(true));
    try {
      const respons =  axios.post<any>(`/project/${project_id}/sync_asana`,query);
      const response =  await respons;
      toast.success(response.data.message);
    } catch (error) {
      showError(error)
      dispatch(setSyncWithJiraError(error));
    } finally {
      dispatch(setSyncWithJiraLoading(false));
    }
  };

  const updateProjectDescription= async (query?:any) => {
    dispatch(setUpdateSimulationLoading(true));
    try {
      const respons =  axios.post<any>(`/project/simulation/update`,query);
      const response =  await respons;
      toast.success(response.data.message);
    } catch (error) {
      showError(error)
      dispatch(setUpdateSimulationError(error));
    } finally {
      dispatch(setUpdateSimulationLoading(false));
    }
  };

  const fetchProjectHistory= async (project_id: string) => {
    dispatch(setProjectHistoryLoading(true));
    try {
      const respons =  axios.get<Project[]>(`/project/${project_id}/project_history`);
      const response =  await respons
      dispatch(setProjectHistory(response.data));
    } catch (error) {
      showError(error)
      dispatch(setProjectHistoryError(error));
    } finally {
      dispatch(setProjectHistoryLoading(false));
    }
  };
  const fetchProjectIntegrations= async (project_id: string) => {
    dispatch(setProjectIntegrationsLoading(true));
    try {
      const respons =  axios.get<Project[]>(`/project/${project_id}/integrations`);
      const response =  await respons
      dispatch(setProjectIntegrations(response.data));
    } catch (error) {
      showError(error)
      dispatch(setProjectIntegrationsError(error));
    } finally {
      dispatch(setProjectIntegrationsLoading(false));
    }
  };

  const fetchProjectStats= async (project_id: string,isnewproject:any = "false") => {
    dispatch(setProjectStatsLoading(true));
    try {
      const respons =  axios.get<Project[]>(`/project/${project_id}/stats?is_newproject=${isnewproject}`);
      const response =  await respons
      dispatch(setProjectStats(response.data));
    } catch (error) {
      showError(error)
      dispatch(setProjectStatsError(error));
    } finally {
      dispatch(setProjectStatsLoading(false));
    }
  };
  const fetchProjectShareTokens= async (project_id:any) => {
    dispatch(setProjectShareLoading(true));
    try {
      const respons =  axios.get<Project[]>(`/project/${project_id}/sharelink`);
      const response =  await respons
      dispatch(setProjectShare(response.data));
    } catch (error) {
      showError(error)
      dispatch(setProjectShareError(error));
    } finally {
      dispatch(setProjectShareLoading(false));
    }
  };

  const handleVersionStore= async (version_id?:any,projectId?:any,sim_flag?:any) => {
    dispatch(setVersionsLoading(true));
    try {
      const respons = axios.post<any>(`/simulation/${projectId}/restore`,{version_id});
      const response =  await respons;
      toast.success(response.data.message);
      await fetchData(projectId,sim_flag,{})
    } catch (error) {
      showError(error)
    } finally {
      dispatch(setVersionsLoading(false));
    }
  };
  const copyProject = async (org_id:String,payload:any) => {
    dispatch(setOrganizationLoading(true));
    try {
      const response = await axios.post<any>(`/project/${payload?.project_id}/copy`,payload);
      if(response.data){
        toast.success(response.data.message);
      }
    } catch (error) {
      showError(error)
      dispatch(setOrganizationError(error));
    } finally {
      dispatch(setOrganizationLoading(false));
    }
  };

  const syncWithAzure= async (project_id: string, query?:any) => {
    dispatch(setSyncWithJiraLoading(true));
    try {
      const respons =  axios.post<any>(`/project/${project_id}/sync_azure`,query);
      const response =  await respons;
      toast.success(response.data.message);
    } catch (error) {
      showError(error)
      dispatch(setSyncWithJiraError(error));
    } finally {
      dispatch(setSyncWithJiraLoading(false));
    }
  };

  const syncWithMonday= async (project_id: string, query?:any) => {
    dispatch(setSyncWithJiraLoading(true));
    try {
      const respons =  axios.post<any>(`/project/${project_id}/sync_monday`,query);
      const response =  await respons;
      toast.success(response.data.message);
    } catch (error) {
      showError(error)
      dispatch(setSyncWithJiraError(error));
    } finally {
      dispatch(setSyncWithJiraLoading(false));
    }
  };

  const fetchProjectNotificationSetting= async (project_id: string) => {
    dispatch(setNotificationSettingLoading(true));
    try {
      const respons =  axios.get<Project[]>(`/notification/project/${project_id}`);
      const response =  await respons;
      dispatch(setNotificationSetting(response?.data));
    } catch (error) {
      showError(error)
      dispatch(setNotificationSettingError(error));
    } finally {
      dispatch(setNotificationSettingLoading(false));
    }
  };

  const updateProjectNotificationSetting= async (project_id: string,payload:any) => {
    dispatch(setNotificationSettingLoading(true));
    try {
      const respons =  axios.put<any>(`/notification/project/${project_id}`,payload);
      const response =  await respons
      toast.success(response.data || "Notification settings updated");
      // Update the Redux state with the new notification settings
      dispatch(setNotificationSetting(payload));
    } catch (error) {
      showError(error)
      dispatch(setNotificationSettingError(error));
    } finally {
      dispatch(setNotificationSettingLoading(false));
    }
  };

  const updateProjectStatusStages = async (project_id: string, status_stages: string) => {
    dispatch(setProjectsLoading(true));
    try {
      const response = await axios({
        method: 'put',
        url: `/project/${project_id}/project_status_stages`,
        data: { status_stages },
        headers: {
          'Content-Type': 'application/json'
        }
      });
      toast.success(response.data.message);
      return response.data;
    } catch (error) {
      showError(error);
      dispatch(setProjectsError(error));
    } finally {
      dispatch(setProjectsLoading(false));
    }
  };

  const fetchProjectById = async (project_id: string) => {
    try {
      const response = await axios.get<Project[]>(`/project/${project_id}`);
      return response.data;
    } catch (error) {
    } finally{
      
    }
  };
  return {
    fetchProjectById,
    fetchProject,
    createProject,
    updateProject,
    updateTaskCompletion,
    createTask,
    createProjectBySimulation,
    fetchTask,
    deleteTask,
    deleteProjects,
    addResources,
    getMappedResources,
    saveMappedResources,
    saveSimulationMerge,
    sendSimulationReset,
    fetchTaskData,
    fetchProjectVersion,
    syncWithJira,
    updateProjectDescription,
    fetchProjectHistory,
    fetchProjectIntegrations,
    syncWithAsana,
    fetchProjectStats,
    fetchProjectShareTokens,
    handleVersionStore,
    copyProject,
    fetchSingleProject,
    clearSingleProject,
    syncWithAzure,
    syncWithMonday,
    fetchProjectNotificationSetting,
    updateProjectNotificationSetting,
    updateProjectStatusStages,
    checkSimulationTasks
  };
};

export default useProjectService;